"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  BookOpen,
  Sparkles,
  Crown,
  Target,
  ShieldCheck,
  Heart,
  Brain,
  Trophy,
  Compass,
  Network,
  Lock,
  Building2,
  ArrowRight,
  GraduationCap,
  Zap,
  Globe2,
  Mic,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const GLOSSARY_TERMS = [
  { term: "Strategic Sovereignty", href: "/glossary/strategic-sovereignty/", description: "The integrated discipline of commanding outcomes across perception, privacy, politics, policing, and policy at scales where failure destabilizes nations.", icon: Crown, accent: "red" },
  { term: "Digital Conglomerate", href: "/glossary/digital-conglomerate/", description: "A multi-domain, technology-integrated enterprise that owns and operates proprietary platforms across interconnected service verticals.", icon: Building2, accent: "purple" },
  { term: "Penta P's Framework", href: "/glossary/penta-ps-framework/", description: "The closed-loop strategic sovereignty system built on five interconnected pillars: Perception, Privacy, Politics, Policing, and Policy.", icon: Network, accent: "cyan" },
  { term: "Sovereign Operations Architecture", href: "/glossary/sovereign-operations-architecture/", description: "The six-stage engagement methodology that governs every sovereign-scale engagement — Intelligence, Analysis, Strategy, Orchestration, Amplification, Feedback.", icon: Compass, accent: "emerald" },
  { term: "Operational Excellence", href: "/glossary/operational-excellence/", description: "The systematic pursuit of flawless execution — the discipline of designing systems so precisely that exceptional outcomes become the default.", icon: Target, accent: "amber-gold" },
  { term: "Leadership as Diagnosis", href: "/glossary/leadership-as-diagnosis/", description: "A clinical operating framework that applies medical method — triage, protocol, outcome accountability — to organizational leadership.", icon: Brain, accent: "denim" },
  { term: "Crisis Transformation", href: "/glossary/crisis-transformation/", description: "The disciplined process of converting a reputational emergency into a position of structural advantage, operating 384x to 1,416x faster than conventional approaches.", icon: Zap, accent: "coral" },
  { term: "eGovernance", href: "/glossary/egovernance/", description: "The use of digital technology, data intelligence, and automated systems to deliver government services and implement policy at national scale.", icon: Globe2, accent: "lime" },
  { term: "Perception Management", href: "/glossary/perception-management/", description: "The systematic engineering of how leaders, governments, and institutions are perceived across every digital surface, news cycle, and public conversation.", icon: Sparkles, accent: "purple" },
  { term: "70% Operating Model", href: "/glossary/70-percent-operating-model/", description: "A hiring framework that selects for five behavioral traits and produces, as an empirical outcome, teams of approximately seventy percent women.", icon: Heart, accent: "emerald" },
];

const CATEGORIES = [
  {
    title: "Strategic Frameworks",
    description: "Foundational concepts that structure sovereign-scale operations.",
    accent: "red",
    terms: ["Strategic Sovereignty", "Penta P's Framework", "Sovereign Operations Architecture", "Digital Conglomerate"],
  },
  {
    title: "Operational Disciplines",
    description: "The methodological frameworks that produce verified outcomes.",
    accent: "purple",
    terms: ["Operational Excellence", "Leadership as Diagnosis", "Crisis Transformation", "Perception Management"],
  },
  {
    title: "Organizational Concepts",
    description: "Team architecture, talent strategy, and governance systems.",
    accent: "cyan",
    terms: ["70% Operating Model", "eGovernance"],
  },
];

export default function GlossaryHubPage() {
  return (
    <>
      <PageHero
        icon={BookOpen}
        overline="Glossary of Terms"
        overlineClassName="text-cyan"
        title={
          <>
            The Operational Vocabulary of <span className="text-gradient-premium">Sovereign-Scale Operations</span>
          </>
        }
        subtitle="The definitive reference for the frameworks, methodologies, and concepts that define sovereign-grade strategic operations. Ten terms — each defined with the precision the subject demands and grounded in fifteen-plus years of verified operational data."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Glossary" }]}
        badge="10 terms · 15+ years"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
            <Sparkles className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">How to Use This Glossary</span>
          </div>
          <h2 className="text-display-md mb-5">A working reference, not a textbook.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The terms defined on this site are not borrowed from consulting literature or academic frameworks. They are operational definitions — extracted from a decade of sovereign-scale operations and refined through engagements where the consequences of failure are measured in national stability, institutional survival, and the lives of millions.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            Each entry provides the formal definition, the operational application, the context in which the term applies, and the related terms that complete the conceptual map. The glossary is organized alphabetically and by thematic category, with cross-references between entries that share conceptual foundations.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Network className="size-3.5 text-red" />
            <span className="text-overline text-red">Thematic Categories</span>
          </div>
          <h2 className="text-display-md mb-4">The terms, organized by domain.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Three thematic categories structure the glossary — strategic frameworks, operational disciplines, and organizational concepts. Each contains the terms that define that domain.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {CATEGORIES.map((cat, i) => {
            const a = ACCENT[cat.accent];
            return (
              <motion.div
                key={cat.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{cat.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed mb-4">{cat.description}</p>
                <ul className="space-y-1.5">
                  {cat.terms.map((t) => (
                    <li key={t} className="text-caption font-semibold text-muted-foreground">· {t}</li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <BookOpen className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Alphabetical Index</span>
          </div>
          <h2 className="text-display-md mb-4">All ten terms, in alphabetical order.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The complete glossary. Each card links to the full definition, operational application, context, and related terms.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {GLOSSARY_TERMS.map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link href={t.href} className="block h-full group">
                  <div className={`relative glass-card rounded-2xl p-6 h-full overflow-hidden bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                    <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                    <div className="relative">
                      <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg ring-2 ${a.ring}`}>
                        <t.icon className="size-6 text-white" />
                      </div>
                      <h3 className={`text-heading-md mb-2 ${a.text}`}>{t.term}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed mb-4">{t.description}</p>
                      <span className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                        Read definition <ArrowRight className="size-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Compass className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Featured Term</span>
          </div>
          <h2 className="text-display-md mb-4">Strategic Sovereignty</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto mb-8">
            The integrated discipline of commanding outcomes across perception, privacy, politics, policing, and policy — at scales where failure destabilizes nations. Not a consulting methodology. Not a technology stack. An operational architecture.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { num: "01", title: "Perception", body: "Reputation, narrative control, crisis response, and digital reputation at national scale.", accent: "red" },
            { num: "02", title: "Privacy", body: "Military-grade encryption, secure communications, counter-surveillance, and data sovereignty.", accent: "purple" },
            { num: "03", title: "Politics", body: "Electoral strategy, voter intelligence, political catalysis, and governance support.", accent: "cyan" },
            { num: "04", title: "Policing", body: "Threat detection, intelligence gathering, defense coordination, and national security operations.", accent: "emerald" },
            { num: "05", title: "Policy", body: "Governance reform, institutional transformation, and smart policy execution.", accent: "amber-gold" },
            { num: "06", title: "Integration", body: "The five pillars share data, intelligence, and learnings across the entire stack — a closed-loop system.", accent: "denim" },
            { num: "07", title: "Orchestration", body: "Nine proprietary platforms coordinated through a single neural command interface.", accent: "lime" },
            { num: "08", title: "Outcome", body: "Verified results — not advisory theory. Campaigns won, nations stabilized, institutions transformed.", accent: "coral" },
          ].map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className={`relative glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="text-display-sm font-bold text-foreground/20 leading-none mb-2">{p.num}</div>
                <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{p.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <BookOpen className="size-10 text-cyan mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Continue exploring.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The FAQ universe answers every question about engagements, methodology, and the operational record. The About page provides the full story of the operational architect.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/faq/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Explore the FAQ
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/about/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Crown className="size-4" />
                About Dr. Jyoti Kush
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "FAQ Hub", description: "Eight subpages covering every domain of expertise.", href: "/faq/", icon: Sparkles, accent: "cyan" },
          { title: "About Dr. Jyoti Kush", description: "The full story. Physician. Operator. Architect.", href: "/about/", icon: Crown, accent: "red" },
          { title: "The 70% Project", description: "The flagship initiative on women in leadership.", href: "/the-70-percent-project/", icon: Heart, accent: "lime" },
          { title: "The Operator's Notebook", description: "Biweekly insights on how excellent things run.", href: "/the-operators-notebook/", icon: BookOpen, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
